/** The list endpoint never returns Promoted — a promoted applicant leaves the overview entirely
 * (see US: Promote an applicant to employee onboarding) — but the value still appears on a
 * detail/promote response. */
export enum ApplicantStatus {
  Submitted = 1,
  Discarded = 2,
  Promoted = 3,
}

/**
 * One row of GET /JobApplicants/{companyId}/Applicants. No branches, no CV flag and none of the
 * employee-only fields exist on an applicant.
 */
export interface IApplicantRow {
  id: number;
  firstName: string;
  lastName: string;
  emailAddress: string;
  phoneNumber: string;
  status: ApplicantStatus;
  submittedDate: string;
}

export interface IApplicantListResponse {
  pageIndex: number;
  pageSize: number;
  numberOfResults: number;
  totalPages: number;
  items: IApplicantRow[];
}

export interface IApplicantListParams {
  companyId: number;
  statuses: ApplicantStatus[];
  name?: string;
  pageIndex: number;
  pageSize: number;
  sortCriteria: number;
  sortOrder: number;
}

/** GET /JobApplicants/{companyId}/Applicants/{id}. Readable with partial branch overlap,
 * unlike Discard/Restore which require managing every branch the applicant is assigned to. */
export interface IApplicantDetail {
  id: number;
  firstName: string;
  lastName: string;
  emailAddress: string;
  phoneNumber: string;
  motivationalText: string;
  status: ApplicantStatus;
  /** 1 = NL, 2 = FR, 3 = EN. */
  languageId: number;
  privacyStatementAcceptedDate: string;
  locationIds: number[];
  cvFileName: string | null;
  cvContentType: string | null;
  cvSizeBytes: number | null;
  submittedDate: string;
  lastModifiedDate: string;
  lastModifiedUser: string;
  /** True when this applicant's email already belongs to an employee under the caller's company. */
  existsAsEmployeeInCompany: boolean;
  /** Only meaningful when existsAsEmployeeInCompany is true — the branches that existing employee
   * already covers, used to preview which of the applicant's requested branches are actually new. */
  existingEmployeeBranchIds: number[];
}

/** POST /JobApplicants/{companyId}/Applicants/{id}/Promote response. Carries just enough for the
 * client to pick the right confirmation wording — the server never prescribes display text. */
export interface IPromoteApplicantResponse {
  outcome: 'NewInvitation' | 'InvitationReused' | 'BranchesAppended';
  employeeId: number | null;
  invitedEmployeeId: number | null;
  addedLocationIds: number[];
  skippedLocationIds: number[];
}

export interface IJobApplicantsState {
  applicants: IApplicantRow[];
  loading: boolean;
  error: string | null;
  numberOfApplicants: number | null;
  totalPages: number | null;
  currentPage: number;
  applicantDetail: IApplicantDetail | null;
  applicantDetailLoading: boolean;
  applicantDetailError: string | null;
  /** Discard/Restore/Promote/branch-edit in-flight state for the currently open applicant
   * profile modal — only one of these actions is ever in flight from that modal at once. */
  applicantActionLoading: boolean;
  applicantActionError: string | null;
  cvDownloadError: boolean;
  /** Outcome-specific confirmation text set after a successful promote, read by Employees.tsx to
   * show a snackbar once the modal has closed. */
  promoteSuccessMessage: string | null;
}
